import React, { useState, useEffect, Fragment } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Container,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    AttachMoney,
    Receipt as ReceiptIconMui,
    VolunteerActivism,
    Undo as RefundIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { ReportChart } from './ReportChart';
import { formatDateForInput, formatDateTime } from '../../utils/dateUtils';

interface FnBOrderRow {
    tabId: string;
    tabNumber: number;
    label: string;
    closedAt: string;
    subtotal: number;
    discountAmount: number;
    tax: number;
    tip: number;
    refunded: number;
    total: number;
    goodsSold: number;
    paymentMethods?: string[];
    memberNames?: string[];
}

/** Totals per payment method, computed per payment leg (a split tab counts under every method that paid it) */
interface FnBPaymentMethodBreakdown {
    method: string;
    legCount: number;
    goodsSold: number;
    tax: number;
    tips: number;
    collected: number;
    refunds: number;
}

interface FnBMemberAccountStatus {
    collected: number;
    refunded: number;
    unbilled: number;
    unbilledCount: number;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
    helcim: 'Card (Terminal)',
    cash: 'Cash',
    member_account: 'Member Account'
};

const paymentMethodLabel = (method: string): string => PAYMENT_METHOD_LABELS[method] || method;

interface FnBOrderDetailAddOn {
    addOnId: string;
    name: string;
    price: number;
    qty: number;
}

interface FnBOrderDetailItem {
    lineId: string;
    name: string;
    price: number;
    quantity: number;
    notes?: string;
    addOns?: FnBOrderDetailAddOn[];
}

// Subset of the full FnBTab row (GET /fnb/orders/:id) that the drill-down modal renders —
// that endpoint also returns payments/refunds/etc., which we don't need here.
interface FnBOrderDetail {
    id: string;
    tabNumber: number;
    label: string;
    closedAt: string | null;
    items: FnBOrderDetailItem[];
    subtotal: number;
    discountAmount: number;
    tax: number;
    total: number;
}

interface FnBReportBucket {
    periodStart: string;
    periodEnd: string;
    goodsSold: number;
    tax: number;
    tips: number;
    orderCount: number;
}

interface FnBFinancialReportData {
    startDate: string;
    endDate: string;
    granularity: 'day' | 'week' | 'month';
    totals: {
        goodsSold: number;
        tax: number;
        tips: number;
        refunds: number;
        orderCount: number;
    };
    byPaymentMethod?: FnBPaymentMethodBreakdown[];
    memberAccount?: FnBMemberAccountStatus;
    buckets: FnBReportBucket[];
    orders: FnBOrderRow[];
    truncated: boolean;
}

const escapeCSVField = (field: any): string => {
    if (field === null || field === undefined) return '""';
    const stringField = String(field);
    return `"${stringField.replace(/"/g, '""')}"`;
};

export const FnBFinancialReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<FnBFinancialReportData | null>(null);
    const [showTransactions, setShowTransactions] = useState(false);

    // Order drill-down modal
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [orderDetail, setOrderDetail] = useState<FnBOrderDetail | null>(null);
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    const [orderDetailError, setOrderDetailError] = useState<string | null>(null);

    // Initialize date range to current month
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    useEffect(() => {
        loadReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            const data = await reportAPI.getFnBFinancialReport({ startDate: startDateStr, endDate: endDateStr });
            setReportData(data);
        } catch (err) {
            setError('Failed to load F&B financial report data');
            console.error('Error loading F&B financial report:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenOrder = async (tabId: string) => {
        setOrderModalOpen(true);
        setOrderDetail(null);
        setOrderDetailError(null);
        setOrderDetailLoading(true);
        try {
            const data = await reportAPI.getFnBOrder(tabId);
            setOrderDetail(data);
        } catch (err) {
            setOrderDetailError('Failed to load order details');
            console.error('Error loading F&B order detail:', err);
        } finally {
            setOrderDetailLoading(false);
        }
    };

    const handleCloseOrderModal = () => {
        setOrderModalOpen(false);
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatBucketLabel = (bucket: FnBReportBucket): string => {
        if (!reportData) return bucket.periodStart;
        if (reportData.granularity === 'day') return bucket.periodStart;
        return `${bucket.periodStart} – ${bucket.periodEnd}`;
    };

    const exportToCSV = () => {
        if (!reportData || reportData.orders.length === 0) return;

        const headers = ['Order #', 'Label', 'Closed At', 'Payment Method(s)', 'Member(s) Charged', 'Subtotal', 'Discount', 'Tax', 'Tip', 'Refunded', 'Total', 'Goods Sold (net)'];
        const rows = reportData.orders.map(o => [
            escapeCSVField(o.tabNumber),
            escapeCSVField(o.label),
            escapeCSVField(formatDateTime(o.closedAt)),
            escapeCSVField((o.paymentMethods ?? []).map(paymentMethodLabel).join(' + ')),
            escapeCSVField((o.memberNames ?? []).join(', ')),
            escapeCSVField(o.subtotal.toFixed(2)),
            escapeCSVField(o.discountAmount.toFixed(2)),
            escapeCSVField(o.tax.toFixed(2)),
            escapeCSVField(o.tip.toFixed(2)),
            escapeCSVField(o.refunded.toFixed(2)),
            escapeCSVField(o.total.toFixed(2)),
            escapeCSVField(o.goodsSold.toFixed(2))
        ].join(','));

        const csvContent = [headers.map(h => escapeCSVField(h)).join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `fnb-financial-report-${reportData.startDate}-to-${reportData.endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading && !reportData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!reportData) {
        return null;
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">F&B Financial Report</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                        <Typography variant="h4">F&B Financial Report</Typography>
                        <Box display="flex" gap={2} alignItems="center">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => newValue && setStartDate(newValue)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => newValue && setEndDate(newValue)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                        </Box>
                    </Box>

                    {/* Key Metrics Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <AttachMoney sx={{ mr: 1, color: 'success.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Goods Sold
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.goodsSold)}
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                        Net of discounts &amp; refunds · {reportData.totals.orderCount} orders
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <ReceiptIconMui sx={{ mr: 1, color: 'info.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Taxes Collected
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.tax)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <VolunteerActivism sx={{ mr: 1, color: 'warning.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Tips Collected
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.tips)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <RefundIcon sx={{ mr: 1, color: 'error.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Refunds
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.refunds)}
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                        Already netted out of Goods Sold
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Trend chart */}
                    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Trend by {reportData.granularity === 'day' ? 'Day' : reportData.granularity === 'week' ? 'Week' : 'Month'}
                        </Typography>
                        {reportData.buckets.length > 0 ? (
                            <ReportChart
                                type="bar"
                                data={{
                                    labels: reportData.buckets.map(formatBucketLabel),
                                    datasets: [
                                        {
                                            label: 'Goods Sold',
                                            data: reportData.buckets.map(b => b.goodsSold),
                                            backgroundColor: '#4CAF50'
                                        },
                                        {
                                            label: 'Tax',
                                            data: reportData.buckets.map(b => b.tax),
                                            backgroundColor: '#2196F3'
                                        },
                                        {
                                            label: 'Tips',
                                            data: reportData.buckets.map(b => b.tips),
                                            backgroundColor: '#FF9800'
                                        }
                                    ]
                                }}
                                height={320}
                                options={{
                                    scales: {
                                        y: { beginAtZero: true }
                                    },
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                label: function (context: any) {
                                                    return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <Typography color="textSecondary" variant="body2">
                                No closed F&amp;B orders in this date range
                            </Typography>
                        )}
                    </Paper>

                    {/* By payment method (per payment leg — split tabs count under each method used) */}
                    {reportData.byPaymentMethod && reportData.byPaymentMethod.length > 0 && (
                        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h6" gutterBottom>By Payment Method</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Split tabs are counted under every method that paid part of them. Member account
                                charges are collected from the member&apos;s card on file at checkout.
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Method</TableCell>
                                            <TableCell align="right">Payments</TableCell>
                                            <TableCell align="right">Goods Sold</TableCell>
                                            <TableCell align="right">Tax</TableCell>
                                            <TableCell align="right">Tips</TableCell>
                                            <TableCell align="right">Refunds</TableCell>
                                            <TableCell align="right">Collected</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.byPaymentMethod.map(m => (
                                            <TableRow key={m.method}>
                                                <TableCell>{paymentMethodLabel(m.method)}</TableCell>
                                                <TableCell align="right">{m.legCount}</TableCell>
                                                <TableCell align="right">{formatCurrency(m.goodsSold)}</TableCell>
                                                <TableCell align="right">{formatCurrency(m.tax)}</TableCell>
                                                <TableCell align="right">{m.tips > 0 ? formatCurrency(m.tips) : '-'}</TableCell>
                                                <TableCell align="right">{m.refunds > 0 ? `-${formatCurrency(m.refunds)}` : '-'}</TableCell>
                                                <TableCell align="right">{formatCurrency(m.collected)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {reportData.memberAccount && reportData.memberAccount.unbilledCount > 0 && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    {reportData.memberAccount.unbilledCount} member account payment(s) totalling{' '}
                                    {formatCurrency(reportData.memberAccount.unbilled)} were recorded before member charges were
                                    billed and were never charged to the member. Run <code>yarn once:find-unbilled-fnb</code> for details.
                                </Alert>
                            )}
                        </Paper>
                    )}

                    {/* Transactions */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <Typography variant="h6">
                                Transactions ({reportData.totals.orderCount})
                            </Typography>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={showTransactions ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    onClick={() => setShowTransactions(v => !v)}
                                    disabled={reportData.orders.length === 0}
                                >
                                    {showTransactions ? 'Hide Transactions' : 'View Transactions'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={exportToCSV}
                                    disabled={reportData.orders.length === 0}
                                >
                                    Export CSV
                                </Button>
                            </Box>
                        </Box>

                        {reportData.truncated && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                This range has more than 5,000 closed orders — showing (and exporting) only the first 5,000.
                                Narrow the date range to see the rest.
                            </Alert>
                        )}

                        {showTransactions && (
                            <TableContainer sx={{ mt: 2, maxHeight: 500 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order #</TableCell>
                                            <TableCell>Label</TableCell>
                                            <TableCell>Closed At</TableCell>
                                            <TableCell>Paid By</TableCell>
                                            <TableCell align="right">Subtotal</TableCell>
                                            <TableCell align="right">Discount</TableCell>
                                            <TableCell align="right">Tax</TableCell>
                                            <TableCell align="right">Tip</TableCell>
                                            <TableCell align="right">Refunded</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.orders.map(o => (
                                            <TableRow key={o.tabId} hover>
                                                <TableCell>
                                                    <Link
                                                        component="button"
                                                        type="button"
                                                        variant="body2"
                                                        underline="hover"
                                                        onClick={() => handleOpenOrder(o.tabId)}
                                                    >
                                                        {o.tabNumber}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    <Link
                                                        component="button"
                                                        type="button"
                                                        variant="body2"
                                                        underline="hover"
                                                        onClick={() => handleOpenOrder(o.tabId)}
                                                    >
                                                        {o.label}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{formatDateTime(o.closedAt)}</TableCell>
                                                <TableCell>
                                                    {(o.paymentMethods ?? []).map(paymentMethodLabel).join(' + ') || '-'}
                                                    {(o.memberNames ?? []).length > 0 && (
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            {(o.memberNames ?? []).join(', ')}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(o.subtotal)}</TableCell>
                                                <TableCell align="right">
                                                    {o.discountAmount > 0 ? `-${formatCurrency(o.discountAmount)}` : '-'}
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(o.tax)}</TableCell>
                                                <TableCell align="right">{o.tip > 0 ? formatCurrency(o.tip) : '-'}</TableCell>
                                                <TableCell align="right">
                                                    {o.refunded > 0 ? `-${formatCurrency(o.refunded)}` : '-'}
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(o.total)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Box>

                {/* Order detail modal */}
                <Dialog open={orderModalOpen} onClose={handleCloseOrderModal} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {orderDetail ? `Order #${orderDetail.tabNumber} — ${orderDetail.label}` : 'Order Details'}
                    </DialogTitle>
                    <DialogContent dividers>
                        {orderDetailLoading && (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {orderDetailError && (
                            <Alert severity="error">{orderDetailError}</Alert>
                        )}

                        {orderDetail && !orderDetailLoading && (
                            <>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Closed {orderDetail.closedAt ? formatDateTime(orderDetail.closedAt) : '—'}
                                </Typography>

                                {orderDetail.items.length === 0 ? (
                                    <Typography color="textSecondary" variant="body2">
                                        No items on this order.
                                    </Typography>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell align="right">Qty</TableCell>
                                                <TableCell align="right">Price</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orderDetail.items.map(item => (
                                                <Fragment key={item.lineId}>
                                                    <TableRow>
                                                        <TableCell>
                                                            {item.name}
                                                            {item.notes && (
                                                                <Typography variant="caption" color="textSecondary" display="block">
                                                                    {item.notes}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">{item.quantity}</TableCell>
                                                        <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                                        <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
                                                    </TableRow>
                                                    {item.addOns?.map(addOn => (
                                                        <TableRow key={addOn.addOnId}>
                                                            <TableCell sx={{ pl: 4 }}>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    + {addOn.name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">{addOn.qty}</TableCell>
                                                            <TableCell align="right">{formatCurrency(addOn.price)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(addOn.price * addOn.qty)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="textSecondary">Subtotal</Typography>
                                        <Typography variant="body2">{formatCurrency(orderDetail.subtotal)}</Typography>
                                    </Box>
                                    {orderDetail.discountAmount > 0 && (
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="body2" color="textSecondary">Discount</Typography>
                                            <Typography variant="body2">-{formatCurrency(orderDetail.discountAmount)}</Typography>
                                        </Box>
                                    )}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2" color="textSecondary">Tax</Typography>
                                        <Typography variant="body2">{formatCurrency(orderDetail.tax)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="subtitle2">Total</Typography>
                                        <Typography variant="subtitle2">{formatCurrency(orderDetail.total)}</Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseOrderModal}>Close</Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>
        </Container>
    );
};
